import {
  ActionReducerMapBuilder,
  createAsyncThunk,
  createAction,
} from '@reduxjs/toolkit';
import { classToPlain } from 'class-transformer';

import { AppError } from '@vk-hr-tek/core/error';
import { showNotification, showError } from '@vk-hr-tek/core/notifications';
import { Notification as NotificationPaylaod } from '@vk-hr-tek/core/notifications/gen/notifications';
import { Socket } from '@vk-hr-tek/core/websocket';

import { BellNotification, BellService } from '@app/gen/bell';

import { ThunkExtra } from '../../../app/store';
import {
  bellNotificationsAdapter,
  BellNotificationsState,
  BellNotificationsWithRootState,
} from '../bellNotifications.state';
import {
  GetBellNotificationsDto,
  MarkReadAllDto,
  MarkReadDto,
} from '../../dto';

export const getBellNotifications = createAsyncThunk<
  BellNotification[],
  GetBellNotificationsDto | undefined,
  ThunkExtra<BellNotificationsWithRootState>
>(
  'bellNotifications/getBellNotifications',
  async (dto, { rejectWithValue, extra: { inject } }) => {
    try {
      const service = inject(BellService);

      const result = await service.getUserBell({
        ...(dto && {
          query: {
            limit: dto.limit,
            last_id: dto.lastId,
            is_read: dto.isRead,
          },
        }),
      });

      return result.bell_notifications;
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const getBellAdditionalListNotifications = createAsyncThunk<
  BellNotification[],
  GetBellNotificationsDto | undefined,
  ThunkExtra<BellNotificationsWithRootState>
>(
  'bellNotifications/getBellAdditionalListNotifications',
  async (dto, { rejectWithValue, extra: { inject } }) => {
    try {
      const service = inject(BellService);

      const result = await service.getUserBell({
        ...(dto && {
          query: {
            limit: dto.limit,
            last_id: dto.lastId,
            is_read: dto.isRead,
          },
        }),
      });

      return result.bell_notifications;
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const getBellCounter = createAsyncThunk<
  number,
  undefined,
  ThunkExtra<BellNotificationsWithRootState>
>(
  'bellNotifications/getBellCounter',
  async (_, { rejectWithValue, extra: { inject } }) => {
    try {
      const service = inject(BellService);

      const result = await service.getUserBellCounter({
        query: { is_read: false },
      });

      return result.bell_notification_counter;
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const markOnBellReadSync = createAction<{
  id: string;
  removeFromList: boolean;
}>('bellNotifications/markOnbellReadSync');

export const markReadOnBell = createAsyncThunk<
  void,
  MarkReadDto,
  ThunkExtra<BellNotificationsWithRootState>
>(
  'bellNotifications/markReadOnBell',
  async (dto, { rejectWithValue, dispatch, extra: { inject } }) => {
    try {
      const service = inject(BellService);

      dispatch(
        markOnBellReadSync({
          id: dto.id,
          removeFromList: !!dto.removeFromList,
        }),
      );

      await service.bellNotificationMarkRead({
        body: { notification_ids: [dto.id] },
      });
    } catch (err) {
      dispatch(
        showError(
          'Не удалось отметить уведомление прочитанным\nПопробуйте еще раз позже',
        ),
      );
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const markBellReadAll = createAsyncThunk<
  void,
  MarkReadAllDto | undefined,
  ThunkExtra<BellNotificationsWithRootState>
>(
  'bellNotifications/markBellReadAll',
  async (_, { rejectWithValue, dispatch, getState, extra: { inject } }) => {
    const { unreadNotificationsCount } = getState().bellNotifications.bell;

    try {
      const service = inject(BellService);

      const response = await service.bellNotificationMarkReadAll();

      dispatch(
        showNotification(
          `Все уведомления отмечены прочитанными (${response.marked_count})`,
        ),
      );
    } catch (err) {
      dispatch(
        showError(
          `Не удалось отметить все уведомления прочитанными (${unreadNotificationsCount})\nПопробуйте еще раз позже`,
        ),
      );
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const updateBellCounter = createAction<{
  counter: number;
}>('bellNotifications/updateBellCounter');

export const bellNotificationsCounterListener = createAsyncThunk<
  void,
  void,
  ThunkExtra
>(
  'bellNotifications/bellNotificationsCounterListener',
  async (_, { rejectWithValue, dispatch, extra: { inject } }) => {
    try {
      const socket = inject(Socket);

      socket.on('notification', (notificationPayload: NotificationPaylaod) => {
        const { payload, object_type: objectType } = notificationPayload;

        if (objectType !== 'bell') {
          return null;
        }

        if (payload && 'unread_messages_count' in payload) {
          const counter = payload.unread_messages_count;

          return dispatch(updateBellCounter({ counter }));
        }
      });
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const bellNotificationActionsReducers = (
  builder: ActionReducerMapBuilder<BellNotificationsState>,
) => {
  builder.addCase(getBellNotifications.pending, (state) => {
    state.bell.status = 'loading';
    state.bell.error = null;
  });
  builder.addCase(
    getBellNotifications.fulfilled,
    (state, { payload, meta }) => {
      state.bell.status = 'complete';
      state.bell.error = null;
      state.bell.currentLastNotificationId = payload[payload.length - 1]?.id;

      if (meta.arg?.limit)
        state.bell.hasMoreNotifications = payload.length >= meta.arg.limit;

      bellNotificationsAdapter.setAll(state.bell, payload);
    },
  );
  builder.addCase(
    getBellNotifications.rejected,
    (state, { payload, error }) => {
      state.bell.status = 'failed';
      bellNotificationsAdapter.removeAll(state.bell);
      state.bell.error =
        /* istanbul ignore next */
        payload ||
        ({
          info: (error && error.message) || 'Unknown error',
          status: 500,
          source: 'client',
          title: 'Internal client error',
        } as AppError);
    },
  );
  builder.addCase(
    getBellAdditionalListNotifications.fulfilled,
    (state, { payload, meta }) => {
      state.bell.currentLastNotificationId = payload[payload.length - 1]?.id;

      if (meta.arg?.limit)
        state.bell.hasMoreNotifications = payload.length >= meta.arg.limit;

      bellNotificationsAdapter.upsertMany(state.bell, payload);
    },
  );
  builder.addCase(getBellCounter.fulfilled, (state, { payload }) => {
    state.bell.unreadNotificationsCount = payload;
  });
  builder.addCase(markOnBellReadSync, (state, { payload }) => {
    if (
      state.page.unreadNotificationsCount &&
      state.page.unreadNotificationsCount > 0
    ) {
      state.page.unreadNotificationsCount =
        state.page.unreadNotificationsCount - 1;
    }

    if (
      state.bell.unreadNotificationsCount &&
      state.bell.unreadNotificationsCount > 0
    ) {
      state.bell.unreadNotificationsCount =
        state.bell.unreadNotificationsCount - 1;
    }

    bellNotificationsAdapter.updateOne(state.page, {
      id: payload.id,
      changes: { read_at: new Date().toISOString() },
    });

    if (payload.removeFromList) {
      bellNotificationsAdapter.removeOne(state.page, payload.id);
    }

    bellNotificationsAdapter.removeOne(state.bell, payload.id);
  });
  builder.addCase(markBellReadAll.pending, (state) => {
    state.bell.isMarkReadAllPending = true;
  });
  builder.addCase(markBellReadAll.fulfilled, (state, { meta }) => {
    bellNotificationsAdapter.removeAll(state.bell);

    if (!!meta.arg?.removeFromList)
      bellNotificationsAdapter.removeAll(state.page);
    else {
      const now = new Date().toISOString();

      const updates = state.page.ids.map((id) => ({
        id,
        changes: { read_at: now },
      }));

      bellNotificationsAdapter.updateMany(state.page, updates);
    }

    state.bell.unreadNotificationsCount = null;
    state.page.unreadNotificationsCount = null;

    state.bell.isMarkReadAllPending = false;
  });
  builder.addCase(markBellReadAll.rejected, (state) => {
    state.bell.isMarkReadAllPending = false;
  });
  builder.addCase(updateBellCounter, (state, { payload }) => {
    state.bell.unreadNotificationsCount = payload.counter;
  });
};
